import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { PillButton } from '@ab-platform/ui/pill-button';
import { FloatingLabelInput } from '@ab-platform/ui/floating-label-input';
import { SelectField } from '@ab-platform/ui/select-field';
import { FocusedPageShell } from '@ab-platform/ui/focused-page-shell';
import { FieldError } from '@ab-platform/ui/field-error';
import { Label } from '@ab-platform/ui/label';
import { Banner } from '@ab-platform/ui/banner';
import { RadioCard } from '@ab-platform/ui/radio-card';
import { CompanyLookupDialog } from '@/components/CompanyLookupDialog';
import { applyCompanyLookupPrefill } from '@/lib/company-lookup-prefill';
import {
  STEP_FIELDS,
  type RegistrationFormValues,
} from '@/hooks/useRegistrationForm';
import {
  useRuiLookup,
  ruiLookupMessage,
  ruiLookupTone,
  ruiLookupKey,
  isRuiLookupStale,
  formatDateIT,
} from '@/hooks/useRuiLookup';
import {
  deriveRuiPrefix,
  normalizeRuiDigits,
  isValidRuiDigits,
  composeCanonicalRui,
} from '@/lib/ruiInput';
import { useWizardSessionIdContext } from '@/hooks/WizardSessionIdContext';

const ORG_TYPE_OPTIONS = ['INTERMEDIARIO', 'BROKER', 'AGENZIA'] as const;
const ORG_TYPE_LABELS: Record<(typeof ORG_TYPE_OPTIONS)[number], string> = {
  INTERMEDIARIO: 'Intermediario sez. E',
  BROKER: 'Broker',
  AGENZIA: 'Agenzia',
};

// Forme giuridiche d'impresa IT — versione TRIM per il public wizard
// onboarding intermediari. Sottoinsieme della lista canonica del
// backoffice (`apps/web-backoffice/src/lib/constants/legal-forms.ts`):
// rimosse `Società semplice (SS)`, `STP` e `Associazione / Fondazione`
// perché irrilevanti per l'iscrizione RUI come intermediario
// assicurativo (richiesta utente, smoke PR-E). Il backoffice mantiene
// la lista completa per anagrafica clienti.
const BUSINESS_LEGAL_FORMS = [
  'Impresa individuale / Imprenditore',
  'Società a responsabilità limitata (SRL)',
  'Società a responsabilità limitata semplificata (SRLS)',
  'Società per azioni (SPA)',
  'Società in accomandita per azioni (SAPA)',
  'Società in nome collettivo (SNC)',
  'Società in accomandita semplice (SAS)',
  'Società cooperativa',
  'Società cooperativa europea',
  'Società europea (SE)',
  'Consorzio',
] as const;

/**
 * Step 1 — organization fundamentals.
 *
 * Same primitives as backoffice: SelectField (Radix dropdown) for closed
 * lists, DateField (custom calendar popover) for dates. No native browser
 * select / date picker — unifies the look across the two surfaces.
 */
export function OrganizationPage(): JSX.Element {
  const navigate = useNavigate();
  const form = useFormContext<RegistrationFormValues>();
  const { register, watch, control, formState, trigger, setValue, getValues } =
    form;
  const errors = formState.errors.organization;
  const legalNature = watch('organization.legalNature');
  const currentVat = watch('organization.vatNumber') ?? '';
  const currentFiscal = watch('organization.fiscalCode') ?? '';
  const isPerson = legalNature === 'PERSON';
  const [lookupOpen, setLookupOpen] = useState<boolean>(false);

  // PR-ONBOARDING-RUI-AUTO-LOOKUP-1 — on-blur RUI verify against the IVASS
  // cache; auto-fills the (read-only) registration date. BE re-verifies at submit.
  const wizardSessionId = useWizardSessionIdContext();
  const ruiLookup = useRuiLookup();
  const orgRuiDate = watch('organization.ruiRegistrationDate');
  // Watch the fields the lookup depends on so the badge invalidates the instant
  // any of them changes (STALE-FIX). `legalNature` is already watched above.
  // The form holds only the 9 numeric digits (UX-INPUT-1); the canonical RUI
  // (section letter + digits) is composed at lookup/submit egress.
  const orgLegalName = watch('organization.legalName');
  const orgType = watch('organization.orgType');
  const orgRuiDigits = watch('organization.ruiNumber') ?? '';
  const orgRuiSection = deriveRuiPrefix(orgType);
  // Show the "9 cifre" error only after blur / Continua — never while the user
  // is still typing digits (UX-INPUT: reset on change, set on blur/continue).
  const [orgRuiTouched, setOrgRuiTouched] = useState<boolean>(false);
  const handleOrgRuiBlur = async (): Promise<void> => {
    const digits = normalizeRuiDigits(getValues('organization.ruiNumber'));
    if (!isValidRuiDigits(digits)) {
      // < 9 digits → nothing to verify yet; clear any prior badge.
      ruiLookup.reset();
      return;
    }
    const currentOrgType = getValues('organization.orgType');
    const r = await ruiLookup.run({
      subject: 'ORGANIZATION',
      orgType: currentOrgType,
      legalNature: getValues('organization.legalNature'),
      ruiNumber: composeCanonicalRui(currentOrgType, digits),
      declaredName: getValues('organization.legalName'),
      wizardSessionId: wizardSessionId ?? undefined,
    });
    if (r?.found && r.registrationDate) {
      setValue('organization.ruiRegistrationDate', r.registrationDate, {
        shouldDirty: true,
        shouldValidate: false,
      });
    }
  };
  // Validity key of the CURRENT inputs vs the one the result was computed for —
  // built from the SAME canonical RUI used at lookup time (STALE-FIX).
  const orgRuiCurrentKey = ruiLookupKey({
    subject: 'ORGANIZATION',
    orgType,
    legalNature,
    ruiNumber: composeCanonicalRui(orgType, orgRuiDigits),
    declaredName: orgLegalName,
  });
  const orgRuiStale = isRuiLookupStale(ruiLookup.state, orgRuiCurrentKey);
  // Stale → tone/message return null → badge hidden while editing (UX-INPUT-1).
  // Re-verification happens on the next blur of RUI/name, or on Continue.
  const orgRuiTone = ruiLookupTone(ruiLookup.state, orgRuiStale);
  const orgRuiMessage = ruiLookupMessage(ruiLookup.state, orgRuiStale);

  const handleContinue = async (): Promise<void> => {
    // Surface the RUI error on Continua even if the field was never blurred.
    setOrgRuiTouched(true);
    // Never advance carrying a stale "verificato": refresh first if the inputs
    // changed since the last lookup (STALE-FIX). BE remains the submit gate.
    if (isValidRuiDigits(orgRuiDigits) && orgRuiStale) {
      await handleOrgRuiBlur();
    }
    const ok = await trigger(
      STEP_FIELDS.organization as unknown as Parameters<typeof trigger>[0],
    );
    if (ok) navigate('/registrazione-intermediario/contatti');
  };

  return (
    <FocusedPageShell
      title="Inizia dalla tua organizzazione"
      description="Bastano i dati essenziali: ti chiediamo il resto solo se serve."
      footer={
        <PillButton
          type="button"
          size="lg"
          onClick={handleContinue}
          className="w-full"
        >
          Continua
        </PillButton>
      }
    >
      <fieldset className="grid gap-2" aria-label="Natura giuridica">
        <Label className="text-xs uppercase tracking-wide text-muted-foreground">
          Natura giuridica
        </Label>
        <div
          className="flex flex-col gap-2 sm:flex-row sm:gap-3"
          role="radiogroup"
        >
          <RadioCard
            value="PERSON"
            label="Persona Fisica"
            description="Intermediario iscritto al RUI come Persona Fisica senza Partita IVA"
            checked={isPerson}
            {...register('organization.legalNature')}
          />
          <RadioCard
            value="COMPANY"
            label="Società"
            description="Intermediario iscritto al RUI come Ditta Individuale o Società"
            checked={!isPerson}
            {...register('organization.legalNature')}
          />
        </div>
      </fieldset>

      {(() => {
        // Re-verify the RUI on legal-name blur: nameMatch depends on it, so a
        // changed name must re-run (or stay invalidated) — STALE-FIX.
        const legalNameReg = register('organization.legalName');
        return (
          <FloatingLabelInput
            label={isPerson ? 'Nome e cognome' : 'Ragione sociale'}
            autoComplete="organization"
            error={errors?.legalName?.message}
            {...legalNameReg}
            onBlur={(e) => {
              void legalNameReg.onBlur(e);
              if ((getValues('organization.ruiNumber') ?? '').trim() !== '') {
                void handleOrgRuiBlur();
              }
            }}
          />
        );
      })()}

      {!isPerson ? (
        <>
          {/* Recupera dati azienda — pre-compilazione via OpenAPI Company.
              Visibile solo per COMPANY: PERSON ha solo CF e nessun
              concetto di "azienda" da arricchire. Il bottone è ghost +
              outline così non compete visivamente con il primary
              "Continua" del footer. */}
          <PillButton
            type="button"
            variant="ghost"
            onClick={() => setLookupOpen(true)}
            className="self-start"
          >
            <Sparkles className="h-4 w-4" aria-hidden />
            Recupera dati azienda
          </PillButton>
          <div className="grid gap-1.5">
            <Controller
              control={control}
              name="organization.businessLegalForm"
              render={({ field }) => (
                <SelectField
                  label="Tipo impresa"
                  value={field.value ?? ''}
                  options={BUSINESS_LEGAL_FORMS as unknown as readonly string[]}
                  onChange={field.onChange}
                  placeholder="—"
                />
              )}
            />
            <FieldError message={errors?.businessLegalForm?.message} />
          </div>
        </>
      ) : null}

      {isPerson ? (
        <FloatingLabelInput
          label="Codice fiscale"
          autoComplete="off"
          error={errors?.fiscalCode?.message}
          // Vedi nota in AdminPage: regex BE `/^[A-Z0-9]{11,16}$/` —
          // normalizziamo a uppercase pre-validazione.
          className="[&_input]:uppercase"
          {...register('organization.fiscalCode', {
            setValueAs: (v: unknown) =>
              typeof v === 'string' ? v.toUpperCase() : v,
          })}
        />
      ) : (
        <div className="grid gap-5 sm:grid-cols-2">
          {(() => {
            const vatReg = register('organization.vatNumber', {
              setValueAs: (v: unknown) =>
                typeof v === 'string' ? v.replace(/\D/g, '') : v,
            });
            return (
              <FloatingLabelInput
                label="Partita IVA"
                inputMode="numeric"
                autoComplete="off"
                error={errors?.vatNumber?.message}
                // P.IVA = `/^[0-9]{11}$/` — strip non-digits in input
                // (l'utente potrebbe pastare "IT 12345678901" o "12.345…").
                {...vatReg}
                // Codice fiscale obbligatorio anche per le società: spesso
                // coincide con la P.IVA → autocompila il CF dalla P.IVA al blur
                // SOLO se vuoto, scrivendolo nel campo (salvato esplicitamente,
                // resta modificabile). PR-ONBOARDING-PUBLIC-SMOKE-FIX-1.
                onBlur={(e) => {
                  void vatReg.onBlur(e);
                  const vat = e.target.value.replace(/\D/g, '');
                  if (
                    vat.length > 0 &&
                    (getValues('organization.fiscalCode') ?? '').trim().length === 0
                  ) {
                    setValue('organization.fiscalCode', vat, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  }
                }}
              />
            );
          })()}
          <FloatingLabelInput
            label="Codice fiscale"
            autoComplete="off"
            error={errors?.fiscalCode?.message}
            className="[&_input]:uppercase"
            {...register('organization.fiscalCode', {
              setValueAs: (v: unknown) =>
                typeof v === 'string' ? v.toUpperCase() : v,
            })}
          />
        </div>
      )}

      <div className="grid gap-1.5">
        <Controller
          control={control}
          name="organization.orgType"
          render={({ field }) => (
            <SelectField
              label="Tipologia"
              value={field.value ?? ''}
              options={ORG_TYPE_OPTIONS}
              onChange={field.onChange}
              renderOption={(v) =>
                ORG_TYPE_LABELS[v as (typeof ORG_TYPE_OPTIONS)[number]] ?? v
              }
            />
          )}
        />
        <FieldError message={errors?.orgType?.message} />
      </div>

      <div className="grid gap-2">
        <div className="grid gap-5 sm:grid-cols-2">
          {/* Numero RUI — la sezione (A/B/E) è il prefisso read-only DENTRO la
              cella; l'utente digita solo le 9 cifre (PR-…-UX-INPUT). */}
          <Controller
            control={control}
            name="organization.ruiNumber"
            render={({ field }) => (
              <FloatingLabelInput
                label="Numero RUI"
                prefix={orgRuiSection ?? undefined}
                inputMode="numeric"
                autoComplete="off"
                maxLength={9}
                value={field.value ?? ''}
                error={orgRuiTouched ? errors?.ruiNumber?.message : undefined}
                onChange={(e) => {
                  field.onChange(normalizeRuiDigits(e.target.value));
                  if (orgRuiTouched) setOrgRuiTouched(false);
                }}
                onBlur={() => {
                  field.onBlur();
                  setOrgRuiTouched(true);
                  void handleOrgRuiBlur();
                }}
              />
            )}
          />
          {/* Data iscrizione: recuperata dal Registro IVASS (read-only),
              non più digitata manualmente (PR-ONBOARDING-RUI-AUTO-LOOKUP-1). */}
          <FloatingLabelInput
            label="Iscrizione RUI"
            labelPosition="top"
            readOnly
            value={formatDateIT(orgRuiDate)}
          />
        </div>
        {orgRuiTone && orgRuiMessage ? (
          <Banner tone={orgRuiTone}>{orgRuiMessage}</Banner>
        ) : null}
      </div>

      {!isPerson ? (
        <FloatingLabelInput
          label="Codice SDI (opzionale)"
          autoComplete="off"
          error={errors?.sdiCode?.message}
          className="[&_input]:uppercase"
          {...register('organization.sdiCode', {
            setValueAs: (v: unknown) =>
              typeof v === 'string' ? v.toUpperCase() : v,
          })}
        />
      ) : null}

      <CompanyLookupDialog
        open={lookupOpen}
        onOpenChange={setLookupOpen}
        defaultVatOrFiscalCode={currentVat || currentFiscal}
        onApply={(company) => applyCompanyLookupPrefill(setValue, company)}
      />
    </FocusedPageShell>
  );
}
